import { useState, useEffect } from "react";
import { getCargoEntry, updateCargoEntry } from "@/lib/api";
import { FIELD_GROUPS, STATUS_STYLES, emptyEntry } from "@/lib/cargoFields";
import { CargoEntryField, fmtCargoVal, withMrnRule } from "@/components/CargoEntryField";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, X } from "lucide-react";
import { toast } from "sonner";

// View a cargo entry and switch to edit mode in place.
export function CargoEntryDialog({ entryId, onClose, onSaved }) {
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCargoEntry(entryId)
      .then((r) => setEntry(r.data))
      .catch(() => { toast.error("Could not load this cargo entry"); onClose(); });
  }, [entryId, onClose]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateCargoEntry(entryId, form);
      toast.success("Entry updated");
      setEntry(r.data);
      setForm(null);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm((p) => withMrnRule(p, k, v));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dash-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {form ? "Edit Cargo Entry" : <span className="font-mono">{entry?.shipment_id || "Cargo entry"}</span>}
            {entry && !form && <span className={`px-2 py-0.5 rounded border text-xs capitalize ${STATUS_STYLES[entry.status] || ""}`}>{entry.status}</span>}
          </DialogTitle>
        </DialogHeader>
        {!entry && <p className="py-8 text-center text-slate-400" data-testid="dash-entry-loading">Loading…</p>}
        {entry && (
          <div className="space-y-5 py-2">
            {FIELD_GROUPS.map((g) => (
              <div key={g.title}>
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">{g.title}</p>
                {form ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {g.fields.map((f) => <CargoEntryField key={f.key} field={f} value={form[f.key]} onChange={setField} />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                    {g.fields.map((f) => (
                      <div key={f.key} className="text-sm" data-testid={`dash-entry-val-${f.key}`}>
                        <span className="text-slate-400">{f.label}: </span>
                        <span className="text-slate-800 font-medium">{fmtCargoVal(f, entry[f.key])}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          {form ? (
            <>
              <Button variant="outline" onClick={() => setForm(null)} data-testid="dash-entry-cancel-btn"><X size={16} className="mr-1" /> Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dash-entry-save-btn">{saving ? "Saving…" : "Update Entry"}</Button>
            </>
          ) : (
            <>
              <Button variant="outline" disabled={!entry} onClick={() => setForm({ ...emptyEntry(), ...entry })} data-testid="dash-entry-edit-btn"><Pencil size={16} className="mr-1" /> Edit</Button>
              <Button onClick={onClose} data-testid="dash-entry-close-btn">Close</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
